import { useEffect, useState } from 'react';
import { Title } from 'react-admin';
import {
  Alert,
  Box,
  Card,
  CardActionArea,
  CardContent,
  CircularProgress,
  Typography,
} from '@mui/material';
import GroupIcon from '@mui/icons-material/Group';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import EventIcon from '@mui/icons-material/Event';
import FavoriteIcon from '@mui/icons-material/Favorite';
import PlaceIcon from '@mui/icons-material/Place';
import ReportIcon from '@mui/icons-material/Report';
import PhotoIcon from '@mui/icons-material/Photo';
import { Link } from 'react-router-dom';
import { staffAction } from '../data/dataProvider';
import { formatDateTime } from '../i18n/labels';
import type { DashboardStats } from '../types';

interface StatCard {
  key: keyof Omit<DashboardStats, 'generatedAt'>;
  label: string;
  icon: JSX.Element;
  /** Куда ведёт клик (страница списка с фильтром); undefined — карточка без перехода. */
  to?: { pathname: string; filter?: Record<string, string> };
  /** Подсветка карточки как «требует внимания» при значении > 0. */
  attention?: boolean;
}

const CARDS: StatCard[] = [
  {
    key: 'usersTotal',
    label: 'Пользователей всего',
    icon: <GroupIcon fontSize="large" color="primary" />,
    to: { pathname: '/users', filter: { status: 'all' } },
  },
  {
    key: 'usersNew7d',
    label: 'Новых за 7 дней',
    icon: <PersonAddIcon fontSize="large" color="primary" />,
    to: { pathname: '/users', filter: { status: 'all' } },
  },
  {
    key: 'activeInvitations',
    label: 'Активных приглашений',
    icon: <EventIcon fontSize="large" color="secondary" />,
    to: { pathname: '/invitations', filter: { status: 'active' } },
  },
  {
    key: 'matchesTotal',
    label: 'Матчей',
    icon: <FavoriteIcon fontSize="large" color="secondary" />,
  },
  {
    key: 'confirmedMeetings',
    label: 'Подтверждённых встреч',
    icon: <PlaceIcon fontSize="large" color="success" />,
  },
  {
    key: 'openReports',
    label: 'Жалоб в очереди',
    icon: <ReportIcon fontSize="large" color="warning" />,
    to: { pathname: '/reports', filter: { status: 'open' } },
    attention: true,
  },
  {
    key: 'pendingPhotos',
    label: 'Фото на модерации',
    icon: <PhotoIcon fontSize="large" color="warning" />,
    to: { pathname: '/photos', filter: { status: 'pending' } },
    attention: true,
  },
];

function cardLink(to: NonNullable<StatCard['to']>): string {
  const search = to.filter ? `?filter=${encodeURIComponent(JSON.stringify(to.filter))}` : '';
  return `${to.pathname}${search}`;
}

/** Стартовая страница: ключевые цифры платформы, клик — переход в раздел. */
export function Dashboard(): JSX.Element {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    staffAction<DashboardStats>('GET', '/staff/dashboard')
      .then(setStats)
      .catch((e: unknown) => setError((e as Error).message));
  }, []);

  return (
    <Box sx={{ p: 2 }}>
      <Title title="Melissa Admin — Дашборд" />
      <Typography variant="h5" sx={{ mb: 2 }}>
        Ключевые метрики
      </Typography>
      {error ? <Alert severity="error">Не удалось загрузить метрики: {error}</Alert> : null}
      {!stats && !error ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : null}
      {stats ? (
        <>
          <Box
            sx={{
              display: 'grid',
              gap: 2,
              gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
            }}
          >
            {CARDS.map((card) => {
              const value = stats[card.key];
              const highlight = card.attention === true && value > 0;
              const content = (
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    {card.icon}
                    <Box>
                      <Typography variant="h4" component="div">
                        {value.toLocaleString('ru-RU')}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {card.label}
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              );
              return (
                <Card
                  key={card.key}
                  sx={highlight ? { borderLeft: 4, borderColor: 'warning.main' } : undefined}
                >
                  {card.to ? (
                    <CardActionArea component={Link} to={cardLink(card.to)}>
                      {content}
                    </CardActionArea>
                  ) : (
                    content
                  )}
                </Card>
              );
            })}
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
            Данные на {formatDateTime(stats.generatedAt)}
          </Typography>
        </>
      ) : null}
    </Box>
  );
}
